"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Target, Database, Blocks, CheckCircle2 } from "lucide-react";

/**
 * 3D-Bühne im Hintergrund + magnetisch einrastende Architektur-Ebenen
 * mit 100% scharfer, horizontaler Schrift.
 */
export default function Preview() {
  const [aligned, setAligned] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setAligned((v) => !v), 2500);
    return () => clearInterval(id);
  }, []);

  const layers = [
    { label: "1. Geschäftsziel", Icon: Target, driftX: -20 },
    { label: "2. Daten & Prozesse", Icon: Database, driftX: 22 },
    { label: "3. Systeme & Teams", Icon: Blocks, driftX: -14 },
  ];

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{
          scale: aligned ? [1, 1.25, 1.1] : 0.85,
          opacity: aligned ? 0.85 : 0.4,
        }}
        transition={{ duration: 0.8 }}
        className={`pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl transition-colors duration-700 ${
          aligned ? "bg-emerald-500/30" : "bg-amber-500/30"
        }`}
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(245,158,11,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(245,158,11,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <div className="relative z-10 flex w-52 flex-col gap-2 antialiased">
        {layers.map(({ label, Icon, driftX }) => (
          <motion.div
            key={label}
            animate={{ x: aligned ? 0 : driftX }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className={`flex items-center justify-between rounded-xl border bg-white px-3.5 py-2 transition-shadow duration-500 dark:bg-zinc-900 ${
              aligned
                ? "border-amber-400/80 shadow-[0_14px_28px_-6px_rgba(245,158,11,0.5)]"
                : "border-black/15 shadow-md dark:border-white/15"
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-amber-500" />
              <span className="font-sans text-xs font-extrabold text-neutral-900 dark:text-white">
                {label}
              </span>
            </div>
            {aligned && (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
